import { useEffect, useId, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Input, Label } from '@/components/ui/Input'
import { parseDecimal } from '@/lib/num'

/** GET /accounting/budgets → `items`: one per expense category and the revenue target. */
export interface BudgetItem {
  category: string
  label: string
  /** null = no plan */
  amount_rub: string | null
  note?: string | null
}

export interface BudgetFormProps {
  /** «октябрь 2026» */
  monthLabel: string
  items: BudgetItem[]
  canEdit: boolean
  busy?: boolean
  error?: string | null
  /** amount_rub '' = remove the plan of that line */
  onSave: (items: { category: string; amount_rub: string }[]) => void
}

/**
 * The month's plan (docs/accounting.md «План-факт»): an amount in ₽ per expense category and the
 * revenue target. An empty field means «нет плана». Only those who enter expenses (owner, admin,
 * finance) may save; the others see the numbers read-only. Every save is audited.
 */
export function BudgetForm({ monthLabel, items, canEdit, busy, error, onSave }: BudgetFormProps) {
  const id = useId()
  const [values, setValues] = useState<Record<string, string>>({})
  useEffect(() => {
    setValues(Object.fromEntries(items.map((i) => [i.category, i.amount_rub === null ? '' : i.amount_rub])))
  }, [items])
  const invalid = Object.entries(values).filter(([, v]) => parseDecimal(v) === undefined).map(([k]) => k)
  const dirty = items.some((i) => (values[i.category] ?? '') !== (i.amount_rub ?? ''))
  return (
    <Card>
      <form
        aria-label={`Бюджет на ${monthLabel}`}
        onSubmit={(e) => {
          e.preventDefault()
          if (!canEdit || invalid.length > 0 || busy) return
          onSave(items.map((i) => ({ category: i.category, amount_rub: parseDecimal(values[i.category] ?? '') ?? '' })))
        }}
      >
        <p className="text-body text-ink-2">Бюджет на {monthLabel}, ₽. Пустое поле — плана нет.</p>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((i) => {
            const bad = invalid.includes(i.category)
            return (
              <div key={i.category}>
                <Label htmlFor={`${id}-${i.category}`}>{i.label}</Label>
                <Input
                  id={`${id}-${i.category}`}
                  inputMode="decimal"
                  className="tnum text-right"
                  value={values[i.category] ?? ''}
                  disabled={!canEdit}
                  placeholder="Нет плана"
                  onChange={(e) => setValues((v) => ({ ...v, [i.category]: e.target.value }))}
                  aria-invalid={bad ? true : undefined}
                />
                {bad && (
                  <p role="alert" className="mt-1 text-caption text-danger">
                    <span aria-hidden>✕ </span>
                    Нужна сумма в рублях, например 15 000,50
                  </p>
                )}
              </div>
            )
          })}
        </div>
        {error && (
          <p role="alert" className="mt-3 text-caption text-danger [overflow-wrap:anywhere]">
            <span aria-hidden>✕ </span>
            {error}
          </p>
        )}
        {canEdit ? (
          <div className="mt-4 flex justify-end">
            <Button type="submit" disabled={!dirty || invalid.length > 0} loading={busy} loadingLabel="Сохраняю…">
              Сохранить бюджет
            </Button>
          </div>
        ) : (
          <p className="mt-3 text-caption text-ink-3">Бюджет меняют владелец, админ и финансист.</p>
        )}
      </form>
    </Card>
  )
}
